import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const rootPath = () => window.Shopify?.routes?.root || '/';

function cartComponents() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')];
}

function sectionIds() {
  return cartComponents()
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .filter((value, index, arr) => arr.indexOf(value) === index);
}

function sectionMode(sectionId) {
  const component = cartComponents().find((el) => el.dataset.sectionId === sectionId);
  return component?.closest('cart-drawer') ? 'hydration' : 'full';
}

async function cartJson() {
  const response = await fetch(`${rootPath()}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Could not read cart');
  return response.json();
}

function findItem(cart, variantId) {
  return (cart.items || []).find((item) => String(item.variant_id) === String(variantId));
}


function findFrameItem(cart, row) {
  const productId = String(row.dataset.frameProductId || '');
  if (!productId) return null;
  return (cart.items || []).find((item) => String(item.product_id) === productId);
}

function currentItem(cart, row) {
  return row.dataset.addonType === 'frame' ? findFrameItem(cart, row) : findItem(cart, row.dataset.variantId);
}

function frameColor(row) {
  const active = row.querySelector('[data-ja-frame-choice].is-active');
  return active?.dataset.frameColor || 'Light brown';
}

function updateFramePreview(row) {
  const selected = frameColor(row);
  row.querySelectorAll('[data-ja-frame-preview]').forEach((button) => {
    button.dataset.frameColor = selected;
    const title = button.dataset.productTitle || '';
    button.setAttribute('aria-label', title ? `See ${title} fullscreen with the selected frame colour` : 'See fullscreen result');
  });
}

async function refreshCartSections() {
  const ids = sectionIds();
  if (!ids.length) return {};

  const rendered = {};
  await Promise.all(ids.map(async (sectionId) => {
    const html = await sectionRenderer.renderSection(sectionId, {
      cache: false,
      mode: sectionMode(sectionId),
    });
    rendered[sectionId] = html;
  }));
  morphReturnedSections(rendered);
  return rendered;
}

function renderLocal(row, active, lineKey = '') {
  row.dataset.active = active ? 'true' : 'false';
  row.dataset.lineKey = lineKey || '';

  const qty = row.querySelector('[data-ja-addon-quantity]');
  const minus = row.querySelector('[data-ja-addon-minus]');
  const plus = row.querySelector('[data-ja-addon-plus]');
  const remove = row.querySelector('[data-ja-addon-remove]');
  const field = row.querySelector('[data-ja-message-field]');

  if (qty) qty.value = active ? '1' : '0';
  if (minus) minus.disabled = !active;
  if (plus) plus.disabled = active;

  if (remove) {
    remove.disabled = !active;
    remove.setAttribute('aria-hidden', active ? 'false' : 'true');
  }

  if (field) field.hidden = !active;
}

function morphReturnedSections(sections = {}) {
  for (const sectionId of sectionIds()) {
    const html = sections?.[sectionId];
    if (!html) continue;
    morphSection(sectionId, html, { mode: sectionMode(sectionId) });
  }
}

async function commit(row, action) {
  const variantId = Number(row.dataset.variantId || 0);
  const type = row.dataset.addonType;
  const status = row.querySelector('[data-ja-addon-status]');
  if (!variantId || row.getAttribute('aria-busy') === 'true') return;

  row.setAttribute('aria-busy', 'true');
  if (status) status.textContent = '';

  try {
    const before = await cartJson();
    const existing = currentItem(before, row);

    // Never trust the visual value. Shopify cart state is authoritative.
    if (action === 'add' && existing) {
      renderLocal(row, true, existing.key || '');
      return;
    }
    if (action !== 'add' && !existing) {
      renderLocal(row, false, '');
      return;
    }

    const ids = sectionIds();
    let response;

    if (action === 'add') {
      response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            // The note starts empty. Frame colour is also stored as an order property
            // in addition to the Shopify variant name for extra clarity.
            ...(type === 'message' ? { properties: {} } : {}),
            ...(type === 'frame' ? { properties: { 'Frame color': frameColor(row) } } : {}),
          }],
          sections: ids.join(','),
          sections_url: window.location.pathname,
        }),
      });
    } else {
      response = await fetch(`${rootPath()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: existing.key,
          quantity: 0,
          sections: ids.join(','),
          sections_url: window.location.pathname,
        }),
      });
    }

    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Cart update failed');
    }

    // Verify Shopify actually accepted the service before changing its visual state.
    const verifiedCart = await cartJson();
    const verifiedItem = currentItem(verifiedCart, row);
    const shouldExist = action === 'add';

    if (Boolean(verifiedItem) !== shouldExist) {
      throw new Error('Shopify did not confirm the cart update');
    }

    // Fetch and render the cart section again from Shopify after the mutation.
    // This avoids stale drawer markup from the Ajax response and means the
    // customer never has to manually refresh to see the new line or total.
    const freshSections = await refreshCartSections();

    // Broadcast after the cart is verified and visibly refreshed.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.documentElement.dispatchEvent(
      new CartLinesUpdateEvent({
        action: shouldExist ? 'add' : 'remove',
        context: 'cart',
        lines: [{
          id: shouldExist ? String(variantId) : existing.key,
          quantity: shouldExist ? 1 : 0,
        }],
        promise: deferred.promise,
      })
    );

    deferred.resolve({
      cart: verifiedCart,
      detail: {
        sections: freshSections,
        items: verifiedCart.items || [],
        itemCount: verifiedCart.item_count || 0,
        source: 'j-artwork-cart-addons',
        didError: false,
      },
    });

    // Morphing may replace the clicked node; re-sync the fresh markup.
    window.setTimeout(syncRows, 80);
  } catch (error) {
    console.warn('J-ARTSWORK cart add-on update failed:', error);

    // Re-read Shopify so a failed request can never leave a false "1" behind.
    try {
      const cart = await cartJson();
      const item = currentItem(cart, row);
      renderLocal(row, Boolean(item), item?.key || '');
    } catch (_) {
      renderLocal(row, false, '');
    }

    if (status) status.textContent = 'Could not update this option. Please try again.';
  } finally {
    row.removeAttribute('aria-busy');
  }
}

async function selectFrameChoice(row, button) {
  const nextVariantId = Number(button.dataset.variantId || row.dataset.variantId || 0);
  const nextColor = button.dataset.frameColor || 'Light brown';
  const status = row.querySelector('[data-ja-addon-status]');
  if (!nextVariantId || row.getAttribute('aria-busy') === 'true') return;

  row.querySelectorAll('[data-ja-frame-choice]').forEach((choice) => {
    const active = choice === button;
    choice.classList.toggle('is-active', active);
    choice.setAttribute('aria-checked', active ? 'true' : 'false');
  });
  row.dataset.variantId = String(nextVariantId);
  updateFramePreview(row);

  try {
    const cart = await cartJson();
    const existing = findFrameItem(cart, row);
    if (!existing) return; // Choice is stored when + is pressed.

    row.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';

    const ids = sectionIds();
    let response;

    if (String(existing.variant_id) === String(nextVariantId)) {
      // One-variant frame products are fully supported: only update the
      // line-item property when the selected colour changes.
      response = await fetch(`${rootPath()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: existing.key,
          quantity: 1,
          properties: { 'Frame color': nextColor },
          sections: ids.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const changed = await response.json();
      if (!response.ok || changed.status || changed.errors) throw new Error('Could not change frame colour');
    } else {
      const removeResponse = await fetch(`${rootPath()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: existing.key, quantity: 0 }),
      });
      const removed = await removeResponse.json();
      if (!removeResponse.ok || removed.status || removed.errors) throw new Error('Could not change frame colour');

      const addResponse = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: nextVariantId,
            quantity: 1,
            properties: { 'Frame color': nextColor },
          }],
        }),
      });
      const added = await addResponse.json();
      if (!addResponse.ok || added.status || added.errors) throw new Error('Could not change frame colour');
    }

    await refreshCartSections();
    window.setTimeout(syncRows, 100);
  } catch (error) {
    console.warn('J-ARTSWORK frame colour update failed:', error);
    if (status) status.textContent = 'Could not change the frame colour. Please try again.';
    window.setTimeout(syncRows, 50);
  } finally {
    row.removeAttribute('aria-busy');
  }
}

async function saveMessage(row) {
  const textarea = row.querySelector('[data-ja-message-text]');
  const status = row.querySelector('[data-ja-addon-status]');
  const variantId = Number(row.dataset.variantId || 0);
  if (!textarea || !variantId) return;

  const message = textarea.value.trim();

  try {
    const cart = await cartJson();
    const item = findItem(cart, variantId);
    if (!item) return;

    const response = await fetch(`${rootPath()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: item.key,
        quantity: 1,
        properties: {
          'Handwritten note': message,
        },
      }),
    });

    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Message update failed');
    }

    row.dataset.lineKey = data.key || item.key;

    if (status) {
      status.textContent = message ? 'Message saved.' : '';
      if (message) {
        window.setTimeout(() => {
          if (status?.textContent === 'Message saved.') status.textContent = '';
        }, 1400);
      }
    }
  } catch (error) {
    console.warn('J-ARTSWORK handwritten note save failed:', error);
    if (status) status.textContent = 'Could not save the message. Please try again.';
  }
}

let messageTimer;

document.addEventListener('click', (event) => {
  const frameChoice = event.target.closest('[data-ja-frame-choice]');
  if (frameChoice) {
    const row = frameChoice.closest('[data-ja-cart-addon]');
    if (!row) return;
    event.preventDefault();
    event.stopPropagation();
    void selectFrameChoice(row, frameChoice);
    return;
  }

  const plus = event.target.closest('[data-ja-addon-plus]');
  const minus = event.target.closest('[data-ja-addon-minus]');
  const remove = event.target.closest('[data-ja-addon-remove]');
  const trigger = plus || minus || remove;
  if (!trigger) return;

  const row = trigger.closest('[data-ja-cart-addon]');
  if (!row) return;

  event.preventDefault();
  event.stopPropagation();

  if (plus) void commit(row, 'add');
  else void commit(row, 'remove');
});

document.addEventListener('input', (event) => {
  const textarea = event.target.closest('[data-ja-message-text]');
  if (!textarea) return;

  const row = textarea.closest('[data-ja-cart-addon]');
  if (!row) return;

  clearTimeout(messageTimer);
  messageTimer = window.setTimeout(() => saveMessage(row), 650);
});

async function syncRows() {
  const rows = [...document.querySelectorAll('[data-ja-cart-addon]')];
  if (!rows.length) return;

  try {
    const cart = await cartJson();

    rows.forEach((row) => {
      const item = currentItem(cart, row);
      renderLocal(row, Boolean(item), item?.key || '');

      if (row.dataset.addonType === 'frame') {
        if (item) {
          row.dataset.variantId = String(item.variant_id);
          const savedColor = item.properties?.['Frame color'] || item.properties?.['Frame colour'] || '';
          row.querySelectorAll('[data-ja-frame-choice]').forEach((button) => {
            const active = savedColor
              ? button.dataset.frameColor === savedColor
              : String(button.dataset.variantId) === String(item.variant_id);
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-checked', active ? 'true' : 'false');
          });
        }
        updateFramePreview(row);
      }

      if (row.dataset.addonType === 'message') {
        const textarea = row.querySelector('[data-ja-message-text]');
        const saved = item?.properties?.['Handwritten note'] || '';
        if (textarea && document.activeElement !== textarea) {
          textarea.value = saved;
        }
      }
    });
  } catch (error) {
    console.warn('J-ARTSWORK cart add-on sync failed:', error);
  }
}

document.addEventListener('DOMContentLoaded', syncRows, { once: true });
document.addEventListener('cart:update', () => window.setTimeout(syncRows, 100));
document.addEventListener('cart:refresh', () => window.setTimeout(syncRows, 100));
window.setTimeout(syncRows, 180);
